/**
 * GDPR-Compliant Safe Storage Wrapper
 * 
 * Questo wrapper garantisce che i dati non essenziali vengano salvati
 * in localStorage solo DOPO che l'utente ha dato il consenso tramite Iubenda.
 * 
 * I dati "necessari" (essential) possono essere salvati senza consenso
 * per il funzionamento base del sito.
 */

// Chiavi considerate "necessarie" per il funzionamento base
// Queste possono essere salvate senza consenso esplicito
const ESSENTIAL_KEYS = new Set([
  // Autenticazione
  'sb-qldjmxfimvxskdbastkx-auth-token',
  'gildy_auth_token',
  
  // Preferenze di accessibilità
  'gildy-accessibility-settings',
  
  // Session ID per operazioni critiche
  'gildy-session-id',
  
  // Preferenze UI funzionali (non dati personali)
  'gildy-filters-collapsed',
  
  // Flusso registrazione email (essenziale per completare signup)
  'gildy_pending_email',
  'gildy_pending_email_at',
  
  // Cache versioning (essenziale per funzionamento cache)
  'cacheVersion',
  
  // OneSignal cleanup/recovery flags (essential for push notification SDK)
  'onesignal_needs_cleanup',
  'onesignal_last_cleanup',
  'onesignal_pending_tags',
  'onesignal_pending_user_id',
  
  // Diamond spreads cross-tab sync (essential for admin operations)
  'diamondSpreadsUpdated',
]);

// Chiavi che richiedono consenso "preferences"
const PREFERENCE_KEYS = new Set([
  'gildy-ui-theme',
  'gildy-ui-metal',
  'gildy-preferred-metal',
  'gildy-preferred-purity',
  'gildy-anonymous-user-id',
  'gildy_anonymous_user_id',
]);

type ConsentChecker = () => boolean;

let globalConsentChecker: ConsentChecker | null = null;

/**
 * Imposta il checker globale per verificare il consenso.
 * Chiamato una volta all'avvio dell'app.
 */
export function setConsentChecker(checker: ConsentChecker): void {
  globalConsentChecker = checker;
}

// Helper per accedere a Iubenda senza conflitti di tipo
interface IubendaObject {
  push?: (args: unknown[]) => void;
  csConfiguration?: {
    consent?: {
      purposes?: Record<number, boolean>;
    };
  };
}

const getIubenda = (): IubendaObject | undefined => {
  if (typeof window !== 'undefined') {
    return (window as unknown as { _iub?: IubendaObject })._iub;
  }
  return undefined;
};

/**
 * Verifica se una chiave è considerata "essenziale"
 */
function isEssentialKey(key: string): boolean {
  // Chiavi Supabase auth sono sempre essenziali
  if (key.startsWith('sb-') && key.includes('-auth-')) {
    return true;
  }
  // Cache funzionali essenziali (dati pubblici, non PII)
  if (key.startsWith('gildy_cache_') ||        // persistentCache
      key.startsWith('unified_cache_') ||       // UnifiedDealerCache
      key.startsWith('gildy_map_cache') ||      // mapDataCache (legacy)
      key.startsWith('gildy_map_bounds_cache') || // mapDataCache v2
      key.startsWith('recommended-dealers-') ||  // dealer cache
      key.startsWith('recommended-dealers-storage') || // enhanced dealer cache
      key.startsWith('fallback-dealers') ||      // dealer fallback
      key.startsWith('fallback_metal_prices') || // metal price fallback
      key.startsWith('price_history_') ||        // price history cache
      key.startsWith('priceLockRequests')) {      // checkpoint requests
    return true;
  }
  return ESSENTIAL_KEYS.has(key);
}

/**
 * Verifica se il consenso è stato dato
 */
function hasPreferenceConsent(): boolean {
  if (globalConsentChecker) {
    return globalConsentChecker();
  }
  
  // Fallback: controlla Iubenda direttamente
  const iub = getIubenda();
  if (iub?.csConfiguration?.consent?.purposes) {
    return iub.csConfiguration.consent.purposes[2] === true;
  }
  
  // Default: permetti solo essential
  return false;
}

/**
 * Safe localStorage wrapper
 */
export const safeLocalStorage = {
  /**
   * Salva un valore in localStorage rispettando le preferenze GDPR
   * @param key - Chiave di storage
   * @param value - Valore da salvare
   * @param forceEssential - Se true, tratta come dato essenziale (default: false)
   */
  setItem(key: string, value: string, forceEssential = false): boolean {
    if (typeof window === 'undefined') return false;
    
    const isEssential = forceEssential || isEssentialKey(key);
    
    if (isEssential || hasPreferenceConsent()) {
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        console.warn(`safeLocalStorage: Failed to save ${key}`, e);
        return false;
      }
    }
    
    // Consenso non dato per chiavi non essenziali
    console.debug(`safeLocalStorage: Skipped ${key} - no consent`);
    return false;
  },

  /**
   * Recupera un valore da localStorage
   */
  getItem(key: string): string | null {
    if (typeof window === 'undefined') return null;
    
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn(`safeLocalStorage: Failed to read ${key}`, e);
      return null;
    }
  },

  /**
   * Rimuove un valore da localStorage
   */
  removeItem(key: string): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`safeLocalStorage: Failed to remove ${key}`, e);
    }
  },

  /**
   * Pulisce tutti i dati non essenziali (per revoca consenso)
   */
  clearNonEssential(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !isEssentialKey(key)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`safeLocalStorage: Cleared ${keysToRemove.length} non-essential items`);
    } catch (e) {
      console.warn('safeLocalStorage: Failed to clear non-essential items', e);
    }
  },
};
